import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Mail, ArrowLeft } from 'lucide-react';

// FAQ knowledge base
const FAQ_DB = [
  // Pricing & Subscriptions
  { keywords: ['price', 'pricing', 'cost', 'how much', 'subscription', 'plan', 'pay', 'fee', 'free', 'tier', 'billing', 'annual', 'monthly'], answer: 'We offer flexible subscription plans for organisations starting from £10/month for up to 10 users. Individual courses may also have one-time prices. Visit our <a href="/pricing" style="color:#20acf6;text-decoration:underline">Pricing page</a> for full details, or register your organisation to see all available plans.' },
  { keywords: ['discount', 'charity', 'offer', 'voucher', 'coupon', 'promo'], answer: 'Registered charities receive 15% off all subscription plans. We also run seasonal promotions. <a href="/contact" style="color:#20acf6;text-decoration:underline">Contact us</a> for details on current offers.' },
  { keywords: ['refund', 'cancel', 'unsubscribe', 'stop'], answer: 'Organisation admins can manage subscriptions from the Subscription page. For cancellation or refund requests, please email <a href="mailto:admin@commonimpactelearning.co.uk" style="color:#20acf6">admin@commonimpactelearning.co.uk</a>.' },
  { keywords: ['trial', 'free trial', 'try', 'demo', 'test'], answer: 'We offer free taster modules on select courses so you can try before you buy. Browse our <a href="/courses" style="color:#20acf6;text-decoration:underline">course catalogue</a> to get started.' },

  // Courses & Content
  { keywords: ['course', 'courses', 'training', 'what courses', 'catalogue', 'available', 'topics', 'subjects'], answer: 'We offer 50+ specialist courses for the care and housing sector, covering safeguarding, health & safety, medication management, infection control, mental health, and more. Browse our full <a href="/courses" style="color:#20acf6;text-decoration:underline">course catalogue</a>.' },
  { keywords: ['safeguarding', 'safeguard'], answer: 'Our Safeguarding courses cover adult safeguarding, children\'s safeguarding, and the six key principles. They\'re designed to meet CQC inspection requirements.' },
  { keywords: ['certificate', 'certification', 'accredited', 'accreditation', 'verified', 'proof', 'cpd'], answer: 'Upon completing a course, you receive a verifiable digital certificate with a unique QR code. Employers and regulators can verify any certificate using our <a href="/certificates/verify" style="color:#20acf6;text-decoration:underline">verification page</a>. Our courses carry recognised CPD accreditation.' },
  { keywords: ['how long', 'duration', 'time', 'hours', 'complete'], answer: 'Course duration varies — most courses take between 1-4 hours to complete. You can work at your own pace and your progress is saved automatically.' },

  // Technical Help
  { keywords: ['login', 'sign in', 'can\'t login', 'password', 'forgot', 'reset', 'locked out'], answer: 'Click "Log in" at the top of the page. If you\'ve forgotten your password, use the "Forgot Password" link on the login page. If you\'re still locked out, email <a href="mailto:admin@commonimpactelearning.co.uk" style="color:#20acf6">admin@commonimpactelearning.co.uk</a>.' },
  { keywords: ['video', 'not playing', 'won\'t play', 'buffering', 'playback', 'loading', 'stuck'], answer: 'If a video won\'t play: 1) Check your internet connection, 2) Try refreshing the page, 3) Clear your browser cache, 4) Try a different browser (Chrome recommended). If the issue persists, <a href="/contact" style="color:#20acf6;text-decoration:underline">contact support</a>.' },
  { keywords: ['download', 'pdf', 'resource', 'file', 'can\'t download'], answer: 'To download resources, click the "Download Resource" button on the element. If downloads fail, check your browser\'s download settings and try again. PDFs should open directly in your browser.' },
  { keywords: ['technical', 'error', 'bug', 'not working', 'problem', 'issue', 'broken', 'help', 'glitch'], answer: 'For technical issues, try refreshing the page first. If the problem persists, please <a href="/contact" style="color:#20acf6;text-decoration:underline">submit an enquiry</a> with details of what happened, including the page URL and any error messages.' },
  { keywords: ['mobile', 'phone', 'tablet', 'app', 'ios', 'android', 'responsive'], answer: 'Our platform is fully responsive and works on all devices through your web browser — no app download needed. For the best experience, use Chrome, Safari, or Edge.' },
  { keywords: ['browser', 'chrome', 'firefox', 'safari', 'edge', 'supported'], answer: 'We support Chrome, Firefox, Safari, and Edge (latest versions). For the best experience, we recommend Google Chrome.' },

  // Account Management
  { keywords: ['register', 'sign up', 'account', 'create account', 'join'], answer: 'You can register as an individual learner or as an organisation. Click "Sign Up" at the top of the page. Organisation admins can then invite team members.' },
  { keywords: ['profile', 'update', 'change email', 'change name', 'settings', 'my account'], answer: 'To update your profile, go to your Dashboard and click on Profile Settings. You can change your name, email, and other details there.' },
  { keywords: ['progress', 'track', 'resume', 'save', 'where', 'left off', 'continue'], answer: 'Your progress is automatically saved. Resume any course from your <a href="/dashboard" style="color:#20acf6;text-decoration:underline">Learner Dashboard</a> — you\'ll pick up right where you left off.' },
  { keywords: ['transcript', 'history', 'record', 'learning record'], answer: 'View your complete learning history on your <a href="/transcript" style="color:#20acf6;text-decoration:underline">Transcript page</a>, including completed courses, certificates, and CPD hours.' },

  // Organisation Management
  { keywords: ['organisation', 'team', 'bulk', 'manage', 'admin', 'company', 'care home', 'staff', 'invite', 'onboard'], answer: 'Organisations can register for team plans, invite staff members, assign courses, and track progress via the Reporting Dashboard. <a href="/register?type=org" style="color:#20acf6;text-decoration:underline">Register your organisation</a> to get started.' },
  { keywords: ['report', 'reporting', 'analytics', 'dashboard', 'compliance', 'cqc'], answer: 'Organisation admins can access compliance reporting from their dashboard — track team completion rates, identify training gaps, and generate CQC-ready reports.' },
  { keywords: ['cqc', 'inspection', 'regulatory', 'mandatory'], answer: 'Our platform supports CQC compliance with mandatory course tracking, automatic renewal reminders, and exportable compliance reports. All courses are aligned with current regulatory requirements.' },
  { keywords: ['invite', 'add user', 'add staff', 'team member'], answer: 'Organisation admins can invite team members from the Team Management page. Staff receive an email invitation to join and can start training immediately.' },

  // About & General
  { keywords: ['about', 'who', 'what is', 'common impact', 'company'], answer: 'Common Impact is a specialist e-learning platform for the care and housing provider sector. We serve social landlords, managing agents, care home operators, councils, and the general public. <a href="/about" style="color:#20acf6;text-decoration:underline">Learn more about us</a>.' },
  { keywords: ['contact', 'support', 'email', 'speak', 'talk', 'human', 'someone', 'reach'], answer: 'You can reach us at <a href="mailto:admin@commonimpactelearning.co.uk" style="color:#20acf6">admin@commonimpactelearning.co.uk</a> or use our <a href="/contact" style="color:#20acf6;text-decoration:underline">Contact page</a>. We respond within 24 hours on business days.' },
  { keywords: ['address', 'office', 'location', 'where', 'based', 'london'], answer: 'Our office is at 71-75 Shelton Street, London, WC2H 9JQ, England.' },
];

const GREETING = "Hi! I'm Ellie, your learning assistant. I can help with questions about pricing, courses, certificates, technical issues, and more. How can I help you today?";

const QUICK_BUTTONS = [
  { label: 'Pricing & Plans', query: 'pricing' },
  { label: 'Courses Available', query: 'courses' },
  { label: 'Certificates', query: 'certificate' },
  { label: 'Technical Help', query: 'technical issue' },
  { label: 'Contact Support', query: 'contact support' },
  { label: 'Organisation Plans', query: 'organisation' },
];

function findAnswer(query) {
  const q = query.toLowerCase().trim();
  let bestMatch = null;
  let bestScore = 0;

  for (const faq of FAQ_DB) {
    let score = 0;
    for (const kw of faq.keywords) {
      if (q.includes(kw)) {
        score += kw.split(' ').length;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = faq;
    }
  }

  if (bestMatch && bestScore > 0) return bestMatch.answer;
  return null;
}

const ChatbotEllie = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'bot', text: GREETING }]);
  const [input, setInput] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSend = (text) => {
    const query = (text || input).trim();
    if (!query) return;
    setInput('');

    setMessages(prev => [...prev, { role: 'user', text: query }]);

    setTimeout(() => {
      const answer = findAnswer(query);
      if (answer) {
        setMessages(prev => [...prev, { role: 'bot', text: answer }]);
        setShowEscalate(false);
      } else {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: "I'm not sure I have the answer to that. You can <a href='/contact' style='color:#20acf6;text-decoration:underline'>submit an enquiry</a> or I can open your email app to contact our support team."
        }]);
        setShowEscalate(true);
      }
    }, 400);
  };

  const handleEscalate = () => {
    const subject = encodeURIComponent('Support Request from Ellie Chat');
    const body = encodeURIComponent(`Hi Common Impact Support,\n\nI need help with the following:\n\n[Please describe your issue here]\n\nThank you.`);
    window.open(`mailto:admin@commonimpactelearning.co.uk?subject=${subject}&body=${body}`, '_blank');
    setMessages(prev => [...prev, { role: 'bot', text: "I've opened your email app with a pre-filled support email. You can also use our <a href='/contact' style='color:#20acf6;text-decoration:underline'>contact form</a>. Our team will get back to you within 24 hours." }]);
    setShowEscalate(false);
  };

  return (
    <>
      {/* Chat Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#20acf6] text-white shadow-lg shadow-[#20acf6]/30 hover:bg-[#3bb8f7] transition-all hover:scale-105 flex items-center justify-center"
          data-testid="chatbot-trigger"
          aria-label="Open chat assistant"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-32px)] h-[500px] max-h-[calc(100vh-48px)] sm:bottom-6 sm:right-6 bottom-0 right-0 sm:w-[360px] w-full sm:h-[500px] h-full sm:rounded-2xl rounded-none bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-[#2a3e5d] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#20acf6] flex items-center justify-center text-sm font-bold">E</div>
              <div>
                <p className="text-sm font-semibold">Ellie</p>
                <p className="text-[10px] text-white/60">Learning Assistant</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chatbot-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" ref={scrollRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#20acf6] text-white rounded-br-sm'
                      : 'bg-[#f8fafc] text-[#1e293b] rounded-bl-sm'
                  }`}
                  data-testid={`chat-msg-${i}`}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              </div>
            ))}

            {/* Quick buttons after greeting */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {QUICK_BUTTONS.map(btn => (
                  <button
                    key={btn.label}
                    onClick={() => handleSend(btn.query)}
                    className="text-xs bg-white border border-[#20acf6]/30 text-[#20acf6] rounded-full px-3 py-1.5 hover:bg-[#20acf6]/10 transition-colors"
                    data-testid={`quick-btn-${btn.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            )}

            {/* Escalation button */}
            {showEscalate && (
              <div className="flex justify-start">
                <button
                  onClick={handleEscalate}
                  className="flex items-center gap-1.5 text-xs bg-[#02d1d4]/10 text-[#02d1d4] border border-[#02d1d4]/30 rounded-full px-3 py-1.5 hover:bg-[#02d1d4]/20 transition-colors"
                  data-testid="escalate-email-btn"
                >
                  <Mail className="h-3.5 w-3.5" /> Email Support Team
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex items-center gap-2"
            >
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 text-sm"
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                size="icon"
                className="bg-[#20acf6] hover:bg-[#3bb8f7] shrink-0"
                disabled={!input.trim()}
                data-testid="chatbot-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotEllie;
